import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
    FichierCompletModel,
    GedCriteresFiltrageGedModelInput,
    MutationGetListeImagesCommonGedArgs,
} from 'src/graphql/generated-types';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { KalDialogRef } from '@kalidea/kaligraphi';
import { catchError, tap } from 'rxjs/operators';
import gql from 'graphql-tag';
import { Store } from '@ngxs/store';
import { throwError } from 'rxjs';
import { SubscriptionManager } from 'src/app/_core/tools/subscription-manager';
import { AppFile } from 'src/app/utils/file.utils';
import { GraphqlService } from 'src/app/_core/services/graphql.service';

@Component({
    selector: 'app-image-bank-list-dialog',
    templateUrl: './image-bank-list-dialog.component.html',
    styleUrls: ['./image-bank-list-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ImageBankListDialogComponent extends SubscriptionManager implements OnInit {

    allowedExtensions = environment.extensions.img;

    picturesList: FichierCompletModel[] = [];

    selectedPicture: FichierCompletModel = null;

    fileUploadControl = new UntypedFormControl();

    loading = false;

    isOpenedFilters = false;

    form: UntypedFormGroup;

    constructor(private readonly dialogRef: KalDialogRef<ImageBankListDialogComponent>,
                private readonly graphqlService: GraphqlService,
                private readonly store: Store,
                private readonly fb: UntypedFormBuilder,
                private readonly cdr: ChangeDetectorRef) {
        super();
    }

    selectImage(image: FichierCompletModel): void {
        this.selectedPicture = image;
    }

    submitImage(image: FichierCompletModel): void {
        this.selectImage(image);
        this.submit();
    }

    submit(uploadedPicture?: AppFile): void {
        this.dialogRef.close({
            selectedPicture: this.selectedPicture,
            uploadedPicture
        });
    }

    close(): void {
        this.dialogRef.close();
    }

    getPictureName(picture: FichierCompletModel): string {
        return picture.Tags.string.join(', ');
    }

    toggleFilters(): void {
        this.isOpenedFilters = !this.isOpenedFilters;
    }

    private getPicturesList(search?: string): void {
        this.loading = true;
        this.cdr.markForCheck();

        const filters: GedCriteresFiltrageGedModelInput = {
            CodeCateg: 'B_BIBLIO',
            RechercheTags: search || ''
        };

        this.graphqlService.mutateOne<'GetListeImagesCommonGED', MutationGetListeImagesCommonGedArgs>({
            mutation: gql`
                mutation getGEDPictures($contexte: ContextInfosInput, $criteres: GEDCriteresFiltrageGEDModelInput) {
                    GetListeImagesCommonGED(contexte: $contexte, criteres: $criteres) {
                        FichierCompletModel {
                            IdFile
                            Url
                            Token
                            FileBytes
                            Filename
                            Extension
                            Tags {
                                string
                            }
                        }
                    }
                }
            `,
            variables: {criteres: filters}
        })
        .pipe(
            this.untilDestroyed,
            tap((GetListeImagesCommonGED) => {
                this.picturesList = GetListeImagesCommonGED?.FichierCompletModel || [];
                this.loading = false;
                this.cdr.markForCheck();
            }),
            catchError(err => {
                this.loading = false;
                this.cdr.markForCheck();
                return throwError(err);
            }),
        ).subscribe();
    }

    private normalizeSearch(formValues: any): string {
        // remove whitespaces and commas
        const searchbar = (formValues.searchbar || '').replace(/\s|,/g, ' ').trim();

        // concat with tag filter
        return searchbar.concat(formValues.tag || '');
    }

    ngOnInit() {
        this.getPicturesList();

        this.form = this.fb.group({
            searchbar: [],
            tag: []
        });

        this.form.valueChanges
            .pipe(
                this.untilDestroyed,
                tap(formValues => this.getPicturesList(this.normalizeSearch(formValues)))
            )
            .subscribe();

        this.fileUploadControl.valueChanges.pipe(
            this.untilDestroyed,
            tap((file: AppFile) => {
                if (this.fileUploadControl.valid) {
                    this.submit(file);
                }
                this.cdr.markForCheck();
            }),
        ).subscribe();
    }

}
